import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { AssistantRoute } from "../api/client";
import type {
  EnemyType,
  GraphicFamily,
  LayerKey,
  Level,
  MaterielFamily,
  MeasureKey,
  Phase,
  Side,
  TerrainReport,
  ZoneLetter,
} from "../api/types";
import { markPhasesTried } from "../app/home/firstStepsState";
import { echoOfRefusal } from "../licence/readOnly";
import { licenceStorage } from "./licenceScope";
import { leaveWith, unsavedDrafts } from "./unsavedDrafts";
import { startPhaseTransition } from "../map/units/phaseTransition";
import { annotationsShown } from "../views/missionDataFlow";

export type ViewKey =
  | "mission"
  | "objects"
  | "manoeuvre"
  | "terrain"
  | "coa"
  | "country"
  | "briefing"
  | "order"
  // OPLAN du corps d'armée (docs/08 §25) : document du niveau corps seulement, juste avant l'OPO.
  | "oplan"
  // Cohérence tactique (docs/24) : constats du moteur et rapport de forces, panneau posé sur la carte.
  | "coherence"
  | "timeline"
  | "evaluation"
  | "exports"
  | "received"
  | "declination"
  | "retex"
  | "logistics"
  // Maintenance (chantier « DTO / CAPOPS », révision « DTO réaliste ») : DTO et CAPOPS, à part du ravitaillement.
  | "maintenance"
  | "orbats"
  // ENI personnalisés de la licence (registre A135) : ouverts depuis le champ « Ennemi » des Données initiales.
  | "enemies";
/** Toutes les vues : une vue mémorisée hors de cette liste est remplacée par la vue par défaut au rechargement
 *  (une clé ajoutée à `ViewKey` doit y figurer, sinon la vue serait perdue). Sert aussi aux tests d'aide. */
export const VIEW_KEYS: readonly ViewKey[] = [
  "mission",
  "objects",
  "manoeuvre",
  "terrain",
  "coa",
  "country",
  "briefing",
  "order",
  "oplan",
  "coherence",
  "timeline",
  "evaluation",
  "exports",
  "received",
  "declination",
  "retex",
  "logistics",
  "maintenance",
  "orbats",
  "enemies",
];
const BASEMAPS: readonly string[] = ["mapbox", "ign-plan", "ign-ortho"];
const PHASES: readonly string[] = ["T1", "T2", "T3"];
const OBJECTS_FILTERS: readonly string[] = ["all", "AMI", "ENI", "graphics", "annotations"];
const HOME_LAYOUTS: readonly string[] = ["grid", "list"];
const HOME_SORTS: readonly string[] = ["recent", "name"];

/** Listes de la carte : ordre de bataille (tous, amis ou ennemis), palettes des graphiques tactiques ou des
 *  annotations APP-6 (ouvertes depuis le panneau « Calques » ; listes des missions, actions ennemies et incidents
 *  retirées le 28/10/2026 avec les « Objets de la mission » de la barre latérale). */
export type ObjectsFilter = "all" | "AMI" | "ENI" | "graphics" | "annotations";
export type Basemap = "mapbox" | "ign-plan" | "ign-ortho";

/** Objet sélectionné sur la carte, affiché dans la fiche d'objet (panneau droit). Les poignées du fuseau en mode
 *  édition (sommet, extrémité ou courbure de LIMA) n'en font pas partie : la fiche d'objet ne s'ouvre pas pour
 *  elles, et leur sélection vit avec l'état de travail du mode édition (`ManoeuvreHandle` de
 *  `map/manoeuvre/useManoeuvre.ts`). */
export type Selection =
  | {
      layer: LayerKey;
      lngLat: [number, number];
      properties: Record<string, unknown>;
    }
  | { layer: "unit"; lngLat: [number, number]; code: string }
  /** Unité d'un niveau inférieur de la carte zoomée (registre J11), par sa clé d'affichage : fiche en lecture seule. */
  | { layer: "subunit"; lngLat: [number, number]; key: string }
  | { layer: "line"; lngLat: [number, number]; name: "L0" | "L1" | "L2" | "L3" }
  | { layer: "graphic"; lngLat: [number, number]; id: string }
  /** Annotation APP-6 (unité non identifiée, population, infrastructure, point de contrôle, événement). */
  | { layer: "annotation"; lngLat: [number, number]; id: string }
  /** Ligne de la ZO (docs/15 §4.3, §4.4) : FLOT et CFL au temps `phase`, les autres lignes sans temps. */
  | { layer: "measure"; lngLat: [number, number]; key: MeasureKey; phase: Phase | null };

/** Paramètres du déploiement affiché : ceux du dossier en cours d'édition (enregistré ou non). */
export interface DeploymentParams {
  /** Dossier enregistré dont le brouillon n'a pas été modifié : son déploiement inclut les déplacements. */
  exercise_id: string | null;
  level: Level;
  map_slug: string;
  odb: string | null;
  mission: string | null;
  enemy_type: EnemyType | null;
  start_dtg: string | null;
  time_zone: ZoneLetter | null;
  /** CENTAC : orientation de l'axe ami → ennemi (degrés). */
  rotation_deg: number | null;
  /** Ordre de bataille ennemi du brouillon ou du dossier (docs/14 §3.5) ; absent ou null : ennemi de référence. */
  eni_odb?: string | null;
  /** Langue du brouillon : toponymes d'une zone libre dans l'aperçu (défaut serveur : celle du dossier `base`). */
  language?: "fr" | "en" | null;
}

/** Mesures de coordination affichées : fuseau (limites latérales), lignes L0 à L3, graphiques tactiques, réseau
 *  de manœuvre, lignes et zones de la ZO (`measures`) et leurs noms (`measureNames` : puces des lignes et noms
 *  des zones, docs/15 §4.2) ; frontières des pays fictifs de la fiche pays (`countryBorders`, demande d'Alexis du
 *  11/10/2026) ; ZEF et ZEA de l'annexe A de l'OPO de division (`fireZones`, demande d'Alexis du 14/10/2026) ;
 *  groupe « Drones » (chantier « Drones ») : VAA (`droneVaa`), ROZ drones (`droneRoz`), NAI (`droneNai`) et éléments
 *  drones de l'analyse du terrain (`droneTerrain`) ; anneaux de visibilité réduite du groupe « Météo »
 *  (`visibility`, `_visibilite/CONCEPTION.md`). Ces cinq calques sont décochés par défaut. */
export type ControlKey =
  | "fuseau"
  | "lima"
  | "graphics"
  | "roads"
  | "measures"
  | "measureNames"
  | "countryBorders"
  | "fireZones"
  | DroneControlKey
  | "visibility"
  | AnnotationControlKey;

/** Calques du groupe « Drones » du panneau Calques (chantier « Drones »). */
export type DroneControlKey = "droneVaa" | "droneRoz" | "droneNai" | "droneTerrain";

/** Calque d'une catégorie d'annotations APP-6 (conception du 15/10/2026, exception à la carte figée). */
export type AnnotationControlKey = "ann_units" | "ann_population" | "ann_infrastructure" | "ann_control" | "ann_events";

export function annotationControl(category: "units" | "population" | "infrastructure" | "control" | "events"): AnnotationControlKey {
  return `ann_${category}`;
}

/** Affichage par défaut des mesures de coordination ; un état mémorisé sur le poste y est fusionné clé par clé
 *  (`mergeFlags`), si bien qu'une couche ajoutée depuis apparaît avec sa valeur par défaut. */
export const DEFAULT_CONTROL_VISIBLE: Readonly<Record<ControlKey, boolean>> = {
  fuseau: true,
  lima: true,
  graphics: true,
  roads: false,
  measures: true,
  measureNames: true,
  countryBorders: true,
  fireZones: true,
  droneVaa: false,
  droneRoz: false,
  droneNai: false,
  droneTerrain: false,
  visibility: false,
  ann_units: true,
  ann_population: true,
  ann_infrastructure: true,
  ann_control: true,
  ann_events: true,
};

/** Réglages d'affichage mémorisés fusionnés aux valeurs par défaut : clés connues seulement, booléens seulement ;
 *  une clé absente (ajoutée depuis) ou illisible prend sa valeur par défaut. */
export function mergeFlags<K extends string>(defaults: Readonly<Record<K, boolean>>, stored: unknown): Record<K, boolean> {
  const out = { ...defaults } as Record<K, boolean>;
  if (!stored || typeof stored !== "object" || Array.isArray(stored)) return out;
  for (const key of Object.keys(defaults) as K[]) {
    const value = (stored as Record<string, unknown>)[key];
    if (typeof value === "boolean") out[key] = value;
  }
  return out;
}

/** Tracé en cours sur la carte : graphique tactique (un clic pose une zone ou un panache NRBC ; une ligne se
 *  trace par clics successifs, double-clic ou Entrée pour finir), ou zone d'analyse du terrain (polygone, trois
 *  sommets au moins). */
export interface Draft {
  /** Zone d'analyse du terrain : polygone, remis au panneau d'analyse à la fin du tracé. */
  purpose?: "terrain";
  /** Famille du graphique, ou « annotation » (symbole APP-6 posé d'un clic, clé du catalogue dans `kind`). */
  family: GraphicFamily | "annotation";
  /** Type de zone ou de ligne, agent NRBC, clé d'annotation. */
  kind: string;
  /** Annotation : SIDC du symbole porté sous le pointeur. */
  sidc?: string;
  /** Nom du type, pour le bandeau de tracé (« Objectif »). */
  name: string;
  color: string;
  /** Aperçu sous le pointeur : rayon de la zone, ou zone R et secteur sous le vent du panache. */
  radius_m?: number;
  downwind_m?: number;
  points: [number, number][];
  hover: [number, number] | null;
  /** Tracé refusé (zone croisée) : consigne affichée dans le bandeau, le tracé continue. */
  error?: string;
}

/** Unité en cours de déplacement sur la carte (aperçu avant enregistrement). */
export interface DragPreview {
  key: string;
  phase: Phase;
  lngLat: [number, number];
}

/** Ligne de coordination en cours de glissement : abscisse proposée le long de l'axe du fuseau. */
export interface LinePreview {
  name: "L0" | "L1" | "L2" | "L3";
  position_m: number;
}

/** Demande d'enregistrement du dossier (déplacement refusé, lien d'une fiche) : les Données initiales
 *  s'affichent et font clignoter « Enregistrer » ; avec `submit`, le dossier est enregistré directement si le
 *  formulaire le permet. `at` date la demande : elle n'est traitée qu'une fois, tant qu'elle est récente. */
export interface SaveRequest {
  at: number;
  submit: boolean;
}

/** Avis flottant au-dessus de la barre basse, avec une action facultative (ex. « Annuler »). */
export interface Notice {
  id: number;
  text: string;
  detail?: string;
  /** `error` : échec d'une action (rouge `danger`) ; `warn` : risque ; `ok` : confirmé ; `info` : le reste. */
  tone: "info" | "ok" | "warn" | "error";
  /** `attention` : action attendue, désignée (clignote) à l'apparition de l'avis. */
  action?: { label: string; run: () => void; attention?: boolean };
  /** Seconde action, après la première (« Annuler » puis « Voir l'ordre » : paragraphes à revoir, docs/18 §6.7). */
  secondary?: { label: string; run: () => void };
}

/** Partie de la rotation CENTAC ouverte dans la vue de l'ordre, hors OPO et FRAGO. */
export type RotationPart = "results" | "wingo";

interface UiState {
  sidebarCollapsed: boolean;
  view: ViewKey;
  objectsFilter: ObjectsFilter;
  exerciseId: string | null;
  /** Camp affiché (décor, grille, assistant, analyse du terrain) : toujours celui du dossier ouvert, ou du brouillon
   *  des Données initiales qui le modifie (`useDossierDisplay` de App.tsx, effet du camp de MissionSummary), jamais
   *  choisi à part. Mémorisé seulement pour l'affichage immédiat à la relance, recalé dès que la liste des dossiers
   *  arrive. */
  mapSlug: string | null;
  layersOpen: boolean;
  visible: Record<LayerKey, boolean>;
  /** Calque « Cours d'eau » sur un terrain libre : masqué par défaut, le fond de carte montre déjà les cours d'eau
   *  (demande d'Alexis du 17/10/2026) ; les camps gardent `visible.rivers`. */
  freeTerrainRivers: boolean;
  basemap: Basemap;
  /** Éclaircissement du fond de carte, de 0 (aucun) à 0,8 : voile blanc sous les couches Generex. */
  mapLighten: number;
  selection: Selection | null;
  assistantOpen: boolean;
  /** Itinéraire tracé par l'assistant (non conservé d'une session à l'autre). */
  assistantRoute: AssistantRoute | null;
  flyTo: { lngLat: [number, number]; zoom: number; nonce: number } | null;
  phase: Phase;
  /** CENTAC : OPO affiché (1 à 4). */
  opo: number;
  /** CENTAC : FRAGO ouvert dans la vue de l'ordre (N°42…), null : l'OPO (docs/08 §27). */
  frago: number | null;
  /** CENTAC : résultats ou WINGO de l'OPO affiché ouverts dans la vue de l'ordre (frise de la rotation), null :
   *  l'OPO ou son FRAGO. */
  rotationPart: RotationPart | null;
  deploymentParams: DeploymentParams | null;
  /** Météo du dossier affiché (brouillon des Données initiales compris), identifiant du serveur (`weatherKey` :
   *  « auto » en CENTAC) : envoyée par la mesure au survol et l'analyse du terrain avec la date et le fuseau de
   *  `deploymentParams`. Hors de ces paramètres : la clé du cache du dispositif n'en dépend pas. */
  weatherKey: string | null;
  unitsVisible: Record<Side, boolean>;
  /** Unités d'appui et de soutien sur la carte (10/10/2026) : cachées par défaut, seule la mêlée paraît. */
  supportUnitsVisible: boolean;
  /** Repère du rôle tactique sous chaque symbole (« 1er éch. », « Rés. »…, docs/25 §4) : affiché par défaut. */
  roleLabelsVisible: boolean;
  controlVisible: Record<ControlKey, boolean>;
  dragPreview: DragPreview | null;
  linePreview: LinePreview | null;
  draft: Draft | null;
  /** Graphique tactique en cours de glissement : décalage (degrés) avant enregistrement. */
  graphicShift: { id: string; dLon: number; dLat: number } | null;
  /** Annotation en cours de glissement : position proposée (temps affiché) avant enregistrement. */
  annotationShift: { id: string; lngLat: [number, number] } | null;
  /** Annotation qui vient d'être posée : sa fiche désigne le champ « Nom » (action attendue). */
  annotationFocus: string | null;
  notice: Notice | null;
  saveRequest: SaveRequest | null;
  /** Vue d'où vient la dernière demande d'enregistrement (`requestSave`) : la vue y revient une fois le dossier
   *  enregistré (`setExercise` du dossier ouvert ou nouvellement créé, docs/14 §2.11) ; oubliée si l'utilisateur
   *  change lui-même de vue ou revient à l'accueil. Non mémorisée sur le poste. */
  returnView: ViewKey | null;
  /** Unité montrée sur la carte depuis l'OPO (survol d'une unité, 09/10/2026) : « Retour à l'OPO » y ramène,
   *  au même endroit de la lecture (`scroll`). Oublié dès que l'utilisateur change lui-même de vue. */
  orderReturn: { scroll: number; view: ViewKey } | null;
  /** Langue du brouillon des Données initiales (null : celle du dossier enregistré). */
  draftLanguage: "fr" | "en" | null;
  /** Écran d'accueil affiché (dossiers, nouveau dossier, import) : à chaque ouverture et à chaque rechargement
   *  de l'application (demande d'Alexis du 29/09/2026), puis depuis l'emblème de la barre latérale. */
  welcome: boolean;
  /** Accueil : dossiers en cartes ou en liste, triés par activité ou par nom (mémorisé sur ce poste). */
  homeLayout: "grid" | "list";
  homeSort: "recent" | "name";
  /** Demande de saisie du nom du dossier (Données initiales), après « Nouveau dossier » depuis l'accueil. */
  nameFocus: number | null;
  /** Analyse du terrain affichée (rapport, zone, praticabilité, axe) et visibilité de son calque. */
  terrain: TerrainReport | null;
  terrainOverlay: boolean;
  /** Zone tracée pour l'analyse du terrain, en attente d'analyse par le panneau. */
  terrainZone: { ring: [number, number][]; at: number } | null;
  /** Incident à ouvrir dans la DIREX (liste des incidents des objets de la mission). */
  direxFocus: string | null;
  /** Famille de matériel à désigner dans la vue Maintenance (chantier « DTO / CAPOPS » : action « Ouvrir la
   *  maintenance » d'un constat de DTO sous la DTO requise ou de la fiche d'unité) ; null : aucune. */
  maintenanceFocus: MaterielFamily | null;
  /** Unité à désigner dans le tableau « CAPOPS des unités » (action « Ouvrir la maintenance » de la fiche d'unité,
   *  par sa clé) ; null : aucune. */
  maintenanceUnit: string | null;
  /** Demande de cadrage sur le dispositif (camp choisi et enregistré dans les Données initiales) : la carte se
   *  recentre une fois de plus sur le barycentre des unités, même si elle l'a déjà fait pour ce dossier et ce camp. */
  frameRequest: number;

  toggleSidebar: () => void;
  setView: (view: ViewKey) => void;
  /** Ouvre l'ordre de bataille sur les objets demandés (« Objets de la mission »). */
  showObjects: (filter: ObjectsFilter) => void;
  /** Carte centrée sur une unité citée par l'OPO, unité sélectionnée ; « Retour à l'OPO » reste offert. */
  showUnitOnMap: (unit: { code: string; lngLat: [number, number] }, scroll: number, from?: ViewKey) => void;
  /** Retour de la carte à l'OPO, à l'endroit de la lecture (`orderReturn` lu puis oublié par la vue). */
  backToOrder: () => void;
  clearOrderReturn: () => void;
  /** Ouvre la DIREX sur un incident ; `null` une fois l'incident montré. */
  focusIncident: (id: string | null) => void;
  /** Ouvre la vue Maintenance sur la famille (parc) ou l'unité désignée ; ni l'une ni l'autre : oublie la
   *  désignation, la vue ne change pas. */
  /** `closeCard` : la fiche d'objet se ferme avec l'ouverture de la vue (fenêtre étroite, où elle masque la colonne
   *  gauche : la vue et la ligne désignée restent visibles). */
  focusMaintenance: (family: MaterielFamily | null, unit?: string | null, closeCard?: boolean) => void;
  setExercise: (id: string | null, mapSlug: string | null) => void;
  setMapSlug: (slug: string | null) => void;
  toggleLayers: () => void;
  setVisible: (layer: LayerKey, visible: boolean) => void;
  setFreeTerrainRivers: (visible: boolean) => void;
  setBasemap: (basemap: Basemap) => void;
  setMapLighten: (mapLighten: number) => void;
  select: (selection: Selection | null) => void;
  toggleAssistant: () => void;
  setAssistantRoute: (route: AssistantRoute | null) => void;
  requestFlyTo: (lngLat: [number, number], zoom: number) => void;
  setSupportUnitsVisible: (visible: boolean) => void;
  setRoleLabelsVisible: (visible: boolean) => void;
  setPhase: (phase: Phase) => void;
  setOpo: (opo: number) => void;
  setFrago: (frago: number | null) => void;
  setRotationPart: (part: RotationPart | null) => void;
  setDeploymentParams: (params: DeploymentParams | null) => void;
  setWeatherKey: (key: string | null) => void;
  setUnitsVisible: (side: Side, visible: boolean) => void;
  setControlVisible: (key: ControlKey, visible: boolean) => void;
  setDragPreview: (preview: DragPreview | null) => void;
  setDraft: (draft: Draft | null) => void;
  setGraphicShift: (shift: { id: string; dLon: number; dLat: number } | null) => void;
  setAnnotationShift: (shift: { id: string; lngLat: [number, number] } | null) => void;
  setAnnotationFocus: (id: string | null) => void;
  setLinePreview: (preview: LinePreview | null) => void;
  notify: (notice: Omit<Notice, "id"> | null) => void;
  /** Demande d'enregistrement : les Données initiales s'affichent ; la vue courante est retenue (`returnView`). */
  requestSave: (submit?: boolean) => void;
  setDraftLanguage: (language: "fr" | "en" | null) => void;
  openWelcome: () => void;
  closeWelcome: () => void;
  setHomeLayout: (layout: "grid" | "list") => void;
  setHomeSort: (sort: "recent" | "name") => void;
  requestNameFocus: () => void;
  setTerrain: (terrain: TerrainReport | null) => void;
  setTerrainOverlay: (visible: boolean) => void;
  setTerrainZone: (ring: [number, number][] | null) => void;
  /** Camp enregistré : cadrage sur le dispositif, mesures de coordination et unités affichées (`annotationsShown`). */
  frameDossier: () => void;
}

/** Stockage du navigateur rangé sous la licence en cours (audit de persistance M8 : le dossier ouvert et le camp
 *  d'une licence ne sont jamais repris par une autre, `state/licenceScope`), protégé : certains contextes
 *  (stockage bloqué, aperçus) lèvent une exception. */
const safeStorage = licenceStorage;

/** Le geste quitte-t-il la vue ou le dossier affichés ? */
function leaves(s: UiState, next: { view?: ViewKey; exerciseId?: string | null }): boolean {
  return (next.view !== undefined && next.view !== s.view) || (next.exerciseId !== undefined && next.exerciseId !== s.exerciseId);
}

/** Changement de vue ou de dossier retenu par le registre des saisies non enregistrées (M4) ; pendant une question
 *  déjà posée, tout geste de navigation se range à la suite (une seule question, gestes rejoués dans l'ordre). */
function guarded(s: UiState, next: { view?: ViewKey; exerciseId?: string | null }, run: () => void): void {
  if (unsavedDrafts.getRequest() || leaves(s, next)) leaveWith(run);
  else run();
}

/** État de l'interface. Mémorisé sur ce poste pour la relance : dossier ouvert, vue, OPO, temps, calques,
 *  fond de carte et panneaux (les dossiers eux-mêmes vivent sur le serveur). */
export const useUi = create<UiState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      view: "mission",
      objectsFilter: "all",
      exerciseId: null,
      mapSlug: null,
      layersOpen: false,
      visible: {
        boundary: true,
        forest: true,
        water: true,
        urban: true,
        rivers: true,
        roads: true,
        bridges: true,
        chokepoints: false,
        grid: false,
      },
      freeTerrainRivers: false,
      basemap: "mapbox",
      mapLighten: 0,
      selection: null,
      assistantOpen: false,
      assistantRoute: null,
      flyTo: null,
      phase: "T1",
      opo: 1,
      frago: null,
      rotationPart: null,
      deploymentParams: null,
      weatherKey: null,
      unitsVisible: { AMI: true, ENI: true, NEUTRE: true },
      supportUnitsVisible: false,
      roleLabelsVisible: true,
      controlVisible: { ...DEFAULT_CONTROL_VISIBLE },
      dragPreview: null,
      draft: null,
      graphicShift: null,
      annotationShift: null,
      annotationFocus: null,
      linePreview: null,
      notice: null,
      saveRequest: null,
      returnView: null,
      orderReturn: null,
      draftLanguage: null,
      welcome: true,
      homeLayout: "grid",
      homeSort: "recent",
      nameFocus: null,
      terrain: null,
      terrainOverlay: true,
      terrainZone: null,
      direxFocus: null,
      maintenanceFocus: null,
      maintenanceUnit: null,
      frameRequest: 0,

      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      // Changer de vue ou de dossier passe par le registre des saisies non enregistrées (audit de persistance M4) :
      // une saisie qui le retient fait d'abord poser la question commune « Abandonner les modifications ? ».
      setView: (view) => guarded(get(), { view }, () => set({ view, returnView: null, orderReturn: null })),
      // Changer de vue (filtres des Objets, incident désigné) oublie la vue retenue par `requestSave`.
      showObjects: (objectsFilter) =>
        guarded(get(), { view: "objects" }, () =>
          set({ view: "objects", objectsFilter, returnView: null, orderReturn: null }),
        ),
      showUnitOnMap: (unit, scroll, from = "order") =>
        guarded(get(), { view: "objects" }, () =>
          set({
            view: "objects",
            objectsFilter: "all",
            returnView: null,
            orderReturn: { scroll, view: from },
            selection: { layer: "unit", lngLat: unit.lngLat, code: unit.code },
            flyTo: { lngLat: unit.lngLat, zoom: 13.5, nonce: Date.now() },
          }),
        ),
      backToOrder: () => {
        const view = get().orderReturn?.view ?? "order";
        guarded(get(), { view }, () => set({ view, returnView: null }));
      },
      clearOrderReturn: () => set({ orderReturn: null }),
      focusIncident: (id) =>
        id
          ? guarded(get(), { view: "timeline" }, () => set({ view: "timeline", direxFocus: id, returnView: null }))
          : set({ direxFocus: null }),
      focusMaintenance: (family, unit = null, closeCard = false) =>
        family || unit
          ? guarded(get(), { view: "maintenance" }, () =>
              set({
                view: "maintenance",
                maintenanceFocus: family,
                maintenanceUnit: unit,
                returnView: null,
                ...(closeCard ? { selection: null } : {}),
              }),
            )
          : set({ maintenanceFocus: null, maintenanceUnit: null }),
      // Réenregistrer le dossier ouvert garde la fiche d'objet (même camp) et l'OPO affichés ; changer de dossier
      // les remet à zéro, abandonne un tracé en cours et oublie le dispositif affiché (celui du dossier ouvert,
      // ou l'aperçu de son brouillon), que la carte recalcule pour le nouveau dossier.
      // Enregistrement demandé depuis une vue (`returnView`) : le dossier enregistré (le même, ou celui qui vient
      // d'être créé) ramène à cette vue ; l'ouverture d'un autre dossier l'oublie. Un dossier existant ouvert alors
      // qu'aucun n'est ouvert passe pour « créé », mais chaque ouverture est suivie de `setView` (liste de la barre
      // latérale, import, transmission) ou précédée de `openWelcome` (accueil), qui oublient la vue retenue.
      setExercise: (exerciseId, mapSlug) =>
        guarded(get(), { exerciseId }, () => set((s) => {
          const saved = exerciseId !== null && (s.exerciseId === exerciseId || s.exerciseId === null);
          const back: Partial<UiState> =
            s.returnView === null ? {} : saved ? { view: s.returnView, returnView: null } : { returnView: null };
          return s.exerciseId !== exerciseId || exerciseId === null
            ? {
                exerciseId,
                mapSlug,
                selection: null,
                opo: 1,
                frago: null,
                rotationPart: null,
                draft: null,
                assistantRoute: null,
                terrain: null,
                terrainZone: null,
                deploymentParams: null,
                weatherKey: null,
                ...back,
              }
            : s.mapSlug === mapSlug
              ? back
              : { mapSlug, selection: null, draft: null, terrain: null, terrainZone: null, ...back };
        })),
      setMapSlug: (mapSlug) =>
        set((s) =>
          s.mapSlug === mapSlug
            ? { mapSlug }
            : { mapSlug, selection: null, draft: null, terrain: null, terrainZone: null },
        ),
      toggleLayers: () => set((s) => ({ layersOpen: !s.layersOpen })),
      setVisible: (layer, visible) =>
        set((s) => ({ visible: { ...s.visible, [layer]: visible } })),
      setFreeTerrainRivers: (freeTerrainRivers) => set({ freeTerrainRivers }),
      setBasemap: (basemap) => set({ basemap }),
      setMapLighten: (mapLighten) =>
        set({ mapLighten: Math.min(0.8, Math.max(0, mapLighten)) }),
      select: (selection) => set({ selection }),
      toggleAssistant: () => set((s) => ({ assistantOpen: !s.assistantOpen })),
      setAssistantRoute: (assistantRoute) => set({ assistantRoute }),
      setSupportUnitsVisible: (supportUnitsVisible) => set({ supportUnitsVisible }),
      setRoleLabelsVisible: (roleLabelsVisible) => set({ roleLabelsVisible }),
      requestFlyTo: (lngLat, zoom) =>
        set({ flyTo: { lngLat, zoom, nonce: Date.now() } }),
      // Changement de temps : la transition des unités part dans le même rendu, depuis le temps quitté.
      setPhase: (phase) => {
        const previous = get().phase;
        if (phase !== previous) {
          startPhaseTransition(previous);
          markPhasesTried(); // premiers pas de l'accueil : « Parcourir T1, T2, T3 »
        }
        set({ phase });
      },
      // Changer d'OPO referme le FRAGO ouvert : il appartient à l'OPO quitté.
      setOpo: (opo) => set((s) => (s.opo === opo ? { opo } : { opo, frago: null, rotationPart: null })),
      setFrago: (frago) => set(frago === null ? { frago } : { frago, rotationPart: null }),
      setRotationPart: (rotationPart) => set(rotationPart === null ? { rotationPart } : { rotationPart, frago: null }),
      setDeploymentParams: (deploymentParams) =>
        set((s) =>
          JSON.stringify(s.deploymentParams) ===
          JSON.stringify(deploymentParams)
            ? s
            : { deploymentParams },
        ),
      setWeatherKey: (weatherKey) => set((s) => (s.weatherKey === weatherKey ? s : { weatherKey })),
      setUnitsVisible: (side, visible) =>
        set((s) => ({ unitsVisible: { ...s.unitsVisible, [side]: visible } })),
      setControlVisible: (key, visible) =>
        set((s) => ({
          controlVisible: { ...s.controlVisible, [key]: visible },
        })),
      setDraft: (draft) => set({ draft }),
      setGraphicShift: (graphicShift) => set({ graphicShift }),
      setAnnotationShift: (annotationShift) => set({ annotationShift }),
      setAnnotationFocus: (annotationFocus) => set({ annotationFocus }),
      setDragPreview: (dragPreview) => set({ dragPreview }),
      setLinePreview: (linePreview) => set({ linePreview }),
      // Avis d'échec qui suit de près un refus de la lecture seule (licence expirée) : écho de ce refus, déjà expliqué
      // ou désigné une fois (`licence/readOnly.ts`) ; pas d'avalanche d'avis.
      notify: (notice) => {
        if (notice && echoOfRefusal(notice.tone)) return;
        set({ notice: notice ? { ...notice, id: Date.now() } : null });
      },
      // Le dossier s'enregistre dans les Données initiales : la vue s'y place pour montrer le bouton.
      // La vue d'où part la demande est retenue (une seconde demande depuis les Données initiales la garde).
      requestSave: (submit = false) =>
        guarded(get(), { view: "mission" }, () => set((s) => ({
          saveRequest: { at: Date.now(), submit },
          view: "mission",
          returnView: s.view !== "mission" ? s.view : s.returnView,
        }))),
      setDraftLanguage: (draftLanguage) => set({ draftLanguage }),
      // L'accueil masque l'espace de travail : un tracé en cours et la palette de recherche n'ont plus de sens.
      openWelcome: () => set({ welcome: true, draft: null, returnView: null }),
      closeWelcome: () => set({ welcome: false }),
      setHomeLayout: (homeLayout) => set({ homeLayout }),
      setHomeSort: (homeSort) => set({ homeSort }),
      requestNameFocus: () => set({ nameFocus: Date.now() }),
      setTerrain: (terrain) => set({ terrain }),
      setTerrainOverlay: (terrainOverlay) => set({ terrainOverlay }),
      setTerrainZone: (ring) => set({ terrainZone: ring ? { ring, at: Date.now() } : null }),
      frameDossier: () =>
        set((s) => ({ ...annotationsShown(s.controlVisible, s.unitsVisible), frameRequest: s.frameRequest + 1 })),
    }),
    {
      name: "generex-ui",
      // v2 : le panneau des calques n'est plus mémorisé (replié à chaque ouverture de l'application).
      version: 2,
      migrate: (persisted) => {
        const { layersOpen: _dropped, ...rest } = (persisted ?? {}) as Record<
          string,
          unknown
        >;
        return rest as unknown as UiState;
      },
      // Valeur mémorisée inconnue (vue renommée, réglage d'une autre version) : valeur par défaut, jamais d'écran
      // blanc.
      merge: (persisted, current) => {
        // `centacSheet` (coupure spéciale du camp de Mailly, retirée le 24/10/2026) : réglage mémorisé oublié.
        const { centacSheet: _sheet, ...p } = (persisted ?? {}) as Partial<UiState> & { centacSheet?: unknown };
        const known = <T>(
          value: T | undefined,
          allowed: readonly string[],
          fallback: T,
        ): T =>
          value !== undefined && allowed.includes(String(value))
            ? value
            : fallback;
        return {
          ...current,
          ...p,
          view: known(p.view, VIEW_KEYS, current.view),
          basemap: known(p.basemap, BASEMAPS, current.basemap),
          phase: known(p.phase, PHASES, current.phase),
          objectsFilter: known(
            p.objectsFilter,
            OBJECTS_FILTERS,
            current.objectsFilter,
          ),
          homeLayout: known(p.homeLayout, HOME_LAYOUTS, current.homeLayout),
          homeSort: known(p.homeSort, HOME_SORTS, current.homeSort),
          // Affichages fusionnés clé par clé : un état mémorisé avant l'ajout d'une couche ne la masque pas.
          visible: mergeFlags(current.visible, p.visible),
          unitsVisible: mergeFlags(current.unitsVisible, p.unitsVisible),
          controlVisible: mergeFlags(current.controlVisible, p.controlVisible),
        };
      },
      storage: createJSONStorage(() => safeStorage),
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        view: s.view,
        objectsFilter: s.objectsFilter,
        homeLayout: s.homeLayout,
        homeSort: s.homeSort,
        exerciseId: s.exerciseId,
        mapSlug: s.mapSlug,
        visible: s.visible,
        freeTerrainRivers: s.freeTerrainRivers,
        basemap: s.basemap,
        mapLighten: s.mapLighten,
        assistantOpen: s.assistantOpen,
        phase: s.phase,
        opo: s.opo,
        unitsVisible: s.unitsVisible,
        supportUnitsVisible: s.supportUnitsVisible,
        roleLabelsVisible: s.roleLabelsVisible,
        controlVisible: s.controlVisible,
      }),
    },
  ),
);
